import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  ButtonGroup,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NotificationManager } from "react-notifications";
import { useParams, useNavigate } from "react-router-dom";
import {
  GetPermissionsByUser,
  UpdatePermission,
} from "../../../services/UsersService";
import CommonLoader from "../../common/CommonLoader";
import UserSelectorDialog from "../../common/UserSelectorDialog";

const EditPermissions = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userSelectorOpen, setUserSelectorOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [formData, setFormData] = useState({
    isSuperAdmin: false,
    isAdmin: false,
    isReportAdmin: false,
    isMaintenanceAdmin: false,
    isBillingAdmin: false,
    isLabAdmin: false,
    isMicroManageAdmin: false,
    isUserManager: false,
    canViewDashboard: false,
    canViewInvoices: false,
    canEditInvoice: false,
    canDeleteInvoice: false,
    canAddInvoice: false,
    canViewQuotes: false,
    canViewOrders: false,
    canEditOrder: false,
    canDeleteOrder: false,
    canAddOrder: false,
    canEditOrderAiSummary: false,
    canViewSamples: false,
    canEditSample: false,
    canDeleteSample: false,
    canAddSample: false,
    canViewResults: false,
    canViewMicroIn: false,
    canEditMicroIn: false,
    canViewMicroOut: false,
    canEditMicroOut: false,
    canViewBatchManager: false,
    canEditBatchManager: false,
    canViewBottleOrders: false,
    canEditBottleOrder: false,
    canDeleteBottleOrder: false,
    canAddBottleOrder: false,
    canUseScanner: false,
    canSearchData: false,
    canViewTestLibs: false,
    canEditTestLib: false,
    canDeleteTestLib: false,
    canAddTestLib: false,
    canViewSlates: false,
    canEditSlate: false,
    canDeleteSlate: false,
    canAddSlate: false,
    canViewTestFormulas: false,
    canEditTestFormula: false,
    canDeleteTestFormula: false,
    canAddTestFormula: false,
    canViewTestCategories: false,
    canEditTestCategory: false,
    canDeleteTestCategory: false,
    canAddTestCategory: false,
    canViewTestInstruments: false,
    canEditTestInstrument: false,
    canDeleteTestInstrument: false,
    canAddTestInstrument: false,
    canViewQualityControlTypes: false,
    canEditQualityControlType: false,
    canDeleteQualityControlType: false,
    canAddQualityControlType: false,
    canViewSampleTypes: false,
    canEditSampleType: false,
    canDeleteSampleType: false,
    canAddSampleType: false,
    canViewSampleMatrices: false,
    canEditSampleMatrix: false,
    canDeleteSampleMatrix: false,
    canAddSampleMatrix: false,
    canViewSampleBottles: false,
    canEditSampleBottle: false,
    canDeleteSampleBottle: false,
    canAddSampleBottle: false,
    canViewTestProcesses: false,
    canEditTestProcess: false,
    canDeleteTestProcess: false,
    canAddTestProcess: false,
    canViewInvoiceCharges: false,
    canEditInvoiceCharge: false,
    canDeleteInvoiceCharge: false,
    canAddInvoiceCharge: false,
    canViewGeneralLedgerCodes: false,
    canEditGeneralLedgerCode: false,
    canDeleteGeneralLedgerCode: false,
    canAddGeneralLedgerCode: false,
    canViewVendors: false,
    canEditVendor: false,
    canDeleteVendor: false,
    canAddVendor: false,
    canViewProducts: false,
    canEditProduct: false,
    canDeleteProduct: false,
    canAddProduct: false,
    canViewSupplyOrders: false,
    canEditSupplyOrder: false,
    canDeleteSupplyOrder: false,
    canAddSupplyOrder: false,
    canUseInventoryTracker: false,
    isWarehouseAdmin: false,
    canViewClients: false,
    canEditClient: false,
    canDeleteClient: false,
    canAddClient: false,
    canViewEquipments: false,
    canEditEquipment: false,
    canDeleteEquipment: false,
    canAddEquipment: false,
    canViewChemicals: false,
    canEditChemical: false,
    canDeleteChemical: false,
    canAddChemical: false,
    canViewEnviroTestKits: false,
    canEditEnviroTestKit: false,
    canDeleteEnviroTestKit: false,
    canAddEnviroTestKit: false,
    canViewWaterProfiles: false,
    canEditWaterProfile: false,
    canDeleteWaterProfile: false,
    canAddWaterProfile: false,
    canViewReports: false,
    canViewOtherReports : false, 
    canViewAdminReports: false,
    canViewUsers: false,
    canEditUser: false,
    canDeleteUser: false,
    canAddUser: false,
    canViewUserRoutes: false,
    canEditUserRoute: false,
    canDeleteUserRoute: false,
    canAddUserRoute: false,
    canViewInstruments: false,
    canViewICPMS: false,
    canViewIC: false,
    canViewATI: false,
    canViewEtkRegistrationQueue: false,
  });
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Group permissions by category for better organization
  const permissionGroups = {
    "Admin Roles": [
      "isSuperAdmin",
      "isAdmin",
      "isReportAdmin",
      "isMaintenanceAdmin",
      "isBillingAdmin",
      "isLabAdmin",
      "isMicroManageAdmin",
      "isUserManager",
    ],
    "Dashboard & General": [
      "canViewDashboard",
      "canUseScanner",
      "canSearchData",
    ],
    "Invoices & Billing": [
      "canViewInvoices",
      "canEditInvoice",
      "canDeleteInvoice",
      "canAddInvoice",
      "canViewInvoiceCharges",
      "canEditInvoiceCharge",
      "canDeleteInvoiceCharge",
      "canAddInvoiceCharge",
    ],
    "Orders & Quotes": [
      "canViewQuotes",
      "canViewOrders",
      "canEditOrder",
      "canDeleteOrder",
      "canAddOrder",
      "canEditOrderAiSummary",
    ],
    "Samples & Results": [
      "canViewSamples",
      "canEditSample",
      "canDeleteSample",
      "canAddSample",
      "canViewResults",
    ],
    Microbiology: [
      "canViewMicroIn",
      "canEditMicroIn",
      "canViewMicroOut",
      "canEditMicroOut",
    ],
    "Batch & Bottle Orders": [
      "canViewBatchManager",
      "canEditBatchManager",
      "canViewBottleOrders",
      "canEditBottleOrder",
      "canDeleteBottleOrder",
      "canAddBottleOrder",
    ],
    "Test Configuration": [
      "canViewTestLibs",
      "canEditTestLib",
      "canDeleteTestLib",
      "canAddTestLib",
      "canViewSlates",
      "canEditSlate",
      "canDeleteSlate",
      "canAddSlate",
      "canViewTestFormulas",
      "canEditTestFormula",
      "canDeleteTestFormula",
      "canAddTestFormula",
      "canViewTestCategories",
      "canEditTestCategory",
      "canDeleteTestCategory",
      "canAddTestCategory",
      "canViewTestInstruments",
      "canEditTestInstrument",
      "canDeleteTestInstrument",
      "canAddTestInstrument",
      "canViewTestProcesses",
      "canEditTestProcess",
      "canDeleteTestProcess",
      "canAddTestProcess",
    ],
    "Quality Control": [
      "canViewQualityControlTypes",
      "canEditQualityControlType",
      "canDeleteQualityControlType",
      "canAddQualityControlType",
    ],
    "Sample Management": [
      "canViewSampleTypes",
      "canEditSampleType",
      "canDeleteSampleType",
      "canAddSampleType",
      "canViewSampleMatrices",
      "canEditSampleMatrix",
      "canDeleteSampleMatrix",
      "canAddSampleMatrix",
      "canViewSampleBottles",
      "canEditSampleBottle",
      "canDeleteSampleBottle",
      "canAddSampleBottle",
    ],
    "Financial Management": [
      "canViewGeneralLedgerCodes",
      "canEditGeneralLedgerCode",
      "canDeleteGeneralLedgerCode",
      "canAddGeneralLedgerCode",
    ],
    "Vendor & Client Management": [
      "canViewVendors",
      "canEditVendor",
      "canDeleteVendor",
      "canAddVendor",
      "canViewClients",
      "canEditClient",
      "canDeleteClient",
      "canAddClient",
    ],
    "Inventory - Products & Supply Orders": [
      "canViewProducts",
      "canEditProduct",
      "canDeleteProduct",
      "canAddProduct",
      "canViewSupplyOrders",
      "canEditSupplyOrder",
      "canDeleteSupplyOrder",
      "canAddSupplyOrder",
    ],
    // ENB-584: the warehouse group, and the Warehouse Admin who gets the reorder notice on the dashboard
    "Inventory - Tracker": [
      "canUseInventoryTracker",
      "isWarehouseAdmin",
    ],
    "Inventory Management": [
      "canViewEquipments",
      "canEditEquipment",
      "canDeleteEquipment",
      "canAddEquipment",
      "canViewChemicals",
      "canEditChemical",
      "canDeleteChemical",
      "canAddChemical",
      "canViewEnviroTestKits",
      "canEditEnviroTestKit",
      "canDeleteEnviroTestKit",
      "canAddEnviroTestKit",
      "canViewWaterProfiles",
      "canEditWaterProfile",
      "canDeleteWaterProfile",
      "canAddWaterProfile",
    ],
    "Reports & Users": [
      "canViewReports",
      "canViewAdminReports",
      "canViewOtherReports",
      "canViewUsers",
      "canEditUser",
      "canDeleteUser",
      "canAddUser",
    ],
    "System Management": [
      "canViewUserRoutes",
      "canEditUserRoute",
      "canDeleteUserRoute",
      "canAddUserRoute",
      "canViewInstruments",
      "canViewICPMS",
      "canViewIC",
      "canViewATI",
      "canViewEtkRegistrationQueue",
    ],
  };

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        if (!userId) return;
        setLoading(true);
        const res = await GetPermissionsByUser(userId);
        const response = res.results;

        if (response) {
          const updatedFormData = { ...formData };
          Object.keys(formData).forEach((key) => {
            updatedFormData[key] = response[key] || false;
          });
          setFormData(updatedFormData);
        }
      } catch (error) {
        NotificationManager.error("Failed to load permissions.");
      } finally {
        setLoading(false);
      }
    };
    loadPermissions();
  }, [userId]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked,
    });
  };

  const handleSelectAll = () => {
    const allSelected = {};
    Object.keys(formData).forEach((key) => {
      allSelected[key] = true;
    });
    setFormData(allSelected);
  };

  const handleUserSelect = async (user) => {
    if (user) {
      setSelectedUser(user.operator || "");

      // Copy current permissions to the selected user
      try {
        setLoading(true);

        // Create the permissions object with the new user ID
        const permissionsToUpdate = {
          userId: user.operator, // Use the selected user's operator ID
          ...formData, // Spread all current permissions
        };

        await UpdatePermission(permissionsToUpdate);

        NotificationManager.success(
          `Permissions successfully copied to user: ${user.operator}`
        );
      } catch (error) {
        NotificationManager.error(
          `Failed to copy permissions to user: ${user.operator}`
        );
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedUser("");
    }
    setUserSelectorOpen(false);
  };

  const handleDeselectAll = () => {
    const allDeselected = {};
    Object.keys(formData).forEach((key) => {
      allDeselected[key] = false;
    });
    setFormData(allDeselected);
  };

  const handleGroupSelectAll = (groupPermissions) => {
    const updatedFormData = { ...formData };
    groupPermissions.forEach((permission) => {
      updatedFormData[permission] = true;
    });
    setFormData(updatedFormData);
  };

  const handleGroupDeselectAll = (groupPermissions) => {
    const updatedFormData = { ...formData };
    groupPermissions.forEach((permission) => {
      updatedFormData[permission] = false;
    });
    setFormData(updatedFormData);
  };

  const getGroupStatus = (groupPermissions) => {
    const selectedCount = groupPermissions.filter(
      (permission) => formData[permission]
    ).length;
    const totalCount = groupPermissions.length;

    if (selectedCount === 0) return { status: "none", text: `0/${totalCount}` };
    if (selectedCount === totalCount)
      return { status: "all", text: `${selectedCount}/${totalCount}` };
    return { status: "partial", text: `${selectedCount}/${totalCount}` };
  };

  const handleSaveClick = async () => {
    try {
      setLoading(true);
      await UpdatePermission({
        userId: userId,
        ...formData,
      });
      NotificationManager.success("Permissions updated successfully.");
      navigate("/vlp/settings-users");
    } catch (error) {
      NotificationManager.error("Failed to update permissions.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate("/vlp/settings-users");
  };

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const handleAccordionChange = (groupName) => (event, isExpanded) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: isExpanded,
    }));
  };

  const totalSelected = Object.values(formData).filter(Boolean).length;
  const totalPermissions = Object.keys(formData).length;

  return (
    <>
      {loading && <CommonLoader loading={loading} />}
      <div className="vlp-component-container">
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
            User Permissions
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Chip
                label={`${totalSelected}/${totalPermissions} Selected`}
                color={totalSelected > 0 ? "primary" : "default"}
                variant="filled"
                size="medium"
              />
              <Button
                variant="outlined"
                color="info"
                onClick={() => setUserSelectorOpen(true)}
                disabled={loading || totalSelected === 0}
                startIcon={<span>📋</span>}
                size="small"
              >
                Copy to User
              </Button>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={handleSelectAll}
                  color="success"
                  startIcon={<span>✓</span>}
                >
                  All
                </Button>
                <Button
                  onClick={handleDeselectAll}
                  color="error"
                  startIcon={<span>✗</span>}
                >
                  None
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </Box>

        {/* Permission Groups Grid */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(permissionGroups).map(([groupName, permissions]) => {
            const groupStatus = getGroupStatus(permissions);
            return (
              <Grid item xs={12} md={6} lg={4} key={groupName}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2.5,
                    height: "100%",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      elevation: 4,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                      pb: 1,
                      borderBottom: "2px solid",
                      borderBottomColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: "1rem",
                        color: "text.primary",
                      }}
                    >
                      {groupName}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={groupStatus.text}
                        size="small"
                        color={
                          groupStatus.status === "all"
                            ? "success"
                            : groupStatus.status === "partial"
                            ? "warning"
                            : "default"
                        }
                        variant="filled"
                      />
                      <ButtonGroup size="small" variant="text">
                        <Button
                          onClick={() => handleGroupSelectAll(permissions)}
                          sx={{ minWidth: "35px", px: 1, fontSize: "0.7rem" }}
                          color="success"
                        >
                          ✓
                        </Button>
                        <Button
                          onClick={() => handleGroupDeselectAll(permissions)}
                          sx={{ minWidth: "35px", px: 1, fontSize: "0.7rem" }}
                          color="error"
                        >
                          ✗
                        </Button>
                      </ButtonGroup>
                    </Box>
                  </Box>

                  {/* Permissions List */}
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    {permissions.map((key, index) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            checked={formData[key]}
                            onChange={handleChange}
                            name={key}
                            size="small"
                            sx={{
                              py: 0.5,
                              "&.Mui-checked": {
                                color: "primary.main",
                              },
                            }}
                          />
                        }
                        label={formatLabel(key)}
                        sx={{
                          display: "flex",
                          m: 0,
                          mb: 0.5,
                          width: "100%",
                          alignItems: "center",
                          p: 0.5,
                          borderRadius: 1,
                          transition: "background-color 0.1s",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          "& .MuiFormControlLabel-label": {
                            fontSize: "0.8rem",
                            lineHeight: 1.3,
                            flex: 1,
                            color: formData[key]
                              ? "primary.main"
                              : "text.secondary",
                            fontWeight: formData[key] ? 500 : 400,
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer Actions */}
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            bottom: 0,
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderTopColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Chip
              label={`${totalSelected}/${totalPermissions} permissions selected`}
              color={totalSelected > 0 ? "primary" : "default"}
              variant="filled"
              size="medium"
            />
            {totalSelected > 0 && (
              <Typography variant="body2" color="text.secondary">
                Ready to save changes
              </Typography>
            )}
          </Box>

          <Box>
            <Button
              variant="outlined"
              onClick={handleCancelClick}
              disabled={loading}
              sx={{ mr: 2, minWidth: 100 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveClick}
              disabled={loading}
              sx={{ minWidth: 120 }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Paper>
      </div>

      <UserSelectorDialog
        open={userSelectorOpen}
        onClose={() => setUserSelectorOpen(false)}
        onSelect={handleUserSelect}
        multiSelect={false}
        filter=""
      />
    </>
  );
};

export default EditPermissions;
